import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Form, type Stored } from "@overmap-ai/common"
import { selectFormsOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useFormSettingsTable, useFormSettingsTableColumns } from "../../components/Tables/FormSettingsTable"
import { Table } from "../../components/Tables/Table"
import { formFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/forms/")

export const FormSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const forms = useShallowEqaulSelector(selectFormsOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(form: Stored<Form>) => {
			void navigate({
				from: "/$organizationId/settings/forms/",
				to: "/$organizationId/settings/forms/$formId",
				params: (prev) => ({ ...prev, formId: form.uuid }),
			})
		},
		[navigate],
	)

	const [filteredForms, filterValue, setFilterValue] = useTextFilter(forms, formFilterCallback)

	const columns = useFormSettingsTableColumns()

	const table = useFormSettingsTable({
		data: filteredForms,
		columns: columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Forms</Heading>
					<Text size="sm" accentColor="base">
						Manage the forms in your organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex flex-col gap-2">
					<div className="flex w-full items-center justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link from="/$organizationId/settings/forms/" to="/$organizationId/settings/forms/create">
							<Button accentColor="primary" variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<Table
						size="sm"
						variant="surface"
						table={{ ...table }}
						onRowClick={handleRowClick}
						interactive
						emptyText="No forms."
					/>
				</div>
			</section>
		</ViewContainer>
	)
})
FormSettingsView.displayName = "FormSettingsView"
